import { Quill } from 'react-quill-new'

const Delta = Quill.import('delta')

/*
This includes code from the quill-magic-url library:

MIT License

Copyright (c) 2020 Jörg Bayreuther

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

const defaults = {
    globalRegularExpression: /(https?:\/\/|www\.)[\S]+/g,
    urlRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)/,
    normalizeRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)/,
    normalizeUrlOptions: {
        stripWWW: false
    }
}

export default class MagicUrl {
    constructor(quill, options) {
        this.quill = quill
        options = options || {}
        this.options = { ...defaults, ...options }
        this.registerTypeListener()
        this.registerPasteListener()
    }

    registerPasteListener() {
        this.quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
            if (typeof node.data !== 'string') {
                return
            }
            const matches = node.data.match(this.options.globalRegularExpression)
            if (matches && matches.length > 0) {
                const newDelta = new Delta()
                let str = node.data
                matches.forEach((match) => {
                    const split = str.split(match)
                    const beforeLink = split.shift()
                    newDelta.insert(beforeLink)
                    newDelta.insert('🔗', { link: match })
                    str = split.join(match)
                })
                newDelta.insert(str)
                delta.ops = newDelta.ops
            }
            return delta
        })
    }

    registerTypeListener() {
        this.quill.on('text-change', (delta) => {
            const ops = delta.ops
            // Only return true, if last operation includes whitespace inserts
            // Equivalent to listening for enter, tab or space
            if (!ops || ops.length < 1 || ops.length > 2) {
                return
            }
            const lastOp = ops[ops.length - 1]
            if (!lastOp.insert || typeof lastOp.insert !== 'string' || !lastOp.insert.match(/\s/)) {
                return
            }
            this.checkTextForUrl()
        })
    }

    checkTextForUrl() {
        const sel = this.quill.getSelection()
        if (!sel) {
            return
        }
        const [leaf] = this.quill.getLeaf(sel.index)
        if (!leaf.text || leaf.parent.domNode.localName === 'a') {
            return
        }
        const urlMatch = leaf.text.match(this.options.urlRegularExpression)
        if (!urlMatch) {
            return
        }
        const leafIndex = this.quill.getIndex(leaf)
        const index = leafIndex + urlMatch.index

        this.textToUrl(index, urlMatch[0])
    }

    textToUrl(index, url) {
        const ops = new Delta().retain(index).delete(url.length).insert('🔗', { link: url })
        this.quill.updateContents(ops)
        this.quill.focus()
        setTimeout(() => this.quill.setSelection(index + 4, 0, 'user'), 0) // I can't seem to set the selection without calling it in a timeout
    }
}
